import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import {
  type AdminStatsOverviewResponse,
  type AdminStatsUsageResponse,
  type AdminUserDetailResponse,
  type AdminUserListResponse,
  adminApiClient,
} from "@/lib/api/client";
import { getApiErrorMessage } from "@/lib/api/errors";

import type { UserFilters } from "./filters";
import { adminKeys } from "./keys";

const USER_USAGE_STALE_MS = 5 * 60 * 1000;
const STATS_OVERVIEW_STALE_MS = 60 * 1000;
const GLOBAL_USAGE_STALE_MS = 5 * 60 * 1000;

async function fetchUsers(filters: UserFilters): Promise<AdminUserListResponse> {
  const { data, error, response } = await adminApiClient.GET("/api/v1/admin/users", {
    params: {
      query: {
        query: filters.query.length > 0 ? filters.query : undefined,
        tier: filters.tier ?? undefined,
        role: filters.role ?? undefined,
        is_active: filters.isActive ?? undefined,
        page: filters.page,
        page_size: filters.pageSize,
      },
    },
  });

  if (!response.ok || error || !data) {
    throw new Error(getApiErrorMessage(error, "Failed to load users."));
  }

  return data;
}

/** Query options for the paginated admin user list (`GET /api/v1/admin/users`). */
export function usersQueryOptions(filters: UserFilters) {
  return queryOptions({
    queryKey: adminKeys.users(filters),
    queryFn: () => fetchUsers(filters),
    placeholderData: keepPreviousData,
  });
}

async function fetchUserDetail(userId: string): Promise<AdminUserDetailResponse> {
  const { data, error, response } = await adminApiClient.GET("/api/v1/admin/users/{user_id}", {
    params: { path: { user_id: userId } },
  });

  if (!response.ok || error || !data) {
    throw new Error(getApiErrorMessage(error, "Failed to load user."));
  }

  return data;
}

async function fetchUsage(
  fromISO: string,
  toISO: string,
  userId?: string,
): Promise<AdminStatsUsageResponse> {
  const { data, error, response } = await adminApiClient.GET("/api/v1/admin/stats/usage", {
    params: {
      query: {
        from: fromISO,
        to: toISO,
        granularity: "day",
        user_id: userId,
      },
    },
  });

  if (!response.ok || error || !data) {
    throw new Error(getApiErrorMessage(error, "Failed to load usage."));
  }

  return data;
}

async function fetchStatsOverview(): Promise<AdminStatsOverviewResponse> {
  const { data, error, response } = await adminApiClient.GET("/api/v1/admin/stats/overview");

  if (!response.ok || error || !data) {
    throw new Error(getApiErrorMessage(error, "Failed to load stats overview."));
  }

  return data;
}

/** Query options for a single admin user (`GET /api/v1/admin/users/{user_id}`). */
export function userDetailQueryOptions(userId: string) {
  return queryOptions({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () => fetchUserDetail(userId),
    enabled: userId.length > 0,
  });
}

/** Query options for per-user usage time series (`GET /api/v1/admin/stats/usage`). */
export function userUsageQueryOptions(userId: string, fromISO: string, toISO: string) {
  return queryOptions({
    queryKey: adminKeys.userUsage(userId, fromISO, toISO),
    queryFn: () => fetchUsage(fromISO, toISO, userId),
    staleTime: USER_USAGE_STALE_MS,
    enabled: userId.length > 0,
  });
}

/** Query options for aggregate dashboard metrics (`GET /api/v1/admin/stats/overview`). */
export function overviewQueryOptions() {
  return queryOptions({
    queryKey: adminKeys.statsOverview,
    queryFn: fetchStatsOverview,
    staleTime: STATS_OVERVIEW_STALE_MS,
  });
}

/** Query options for platform-wide usage time series (`GET /api/v1/admin/stats/usage`). */
export function globalUsageQueryOptions(fromISO: string, toISO: string) {
  return queryOptions({
    queryKey: adminKeys.globalUsage(fromISO, toISO),
    queryFn: () => fetchUsage(fromISO, toISO),
    staleTime: GLOBAL_USAGE_STALE_MS,
  });
}
